import { Box, Grid, Tooltip } from "@mui/material"
import React, { useRef, useState } from "react"
import TransparentImageIcon from "assets/icons/TransparentImageIcon.svg"
import { bytesToSize, removeDecimalUpto1 } from "helpers/functions"
import StyledLabel from "components/StyledComponents/StyledLabel"
import AppButtonGradient from "components/StyledComponents/AppButtonGradient"
import AppButton from "components/StyledComponents/AppButton"
import InfoIcon from "@mui/icons-material/Info"
import { Cancel } from "@mui/icons-material"
import styles from "./FileUploadWithPreview.module.scss"
import { UploadPreviewFieldContainer } from "./PreviewFieldContainer"

const ImageUploadPreview = ({
  label,
  isRequired,
  placeholder = "",
  error,
  value = [],
  onChange = () => {},
  onDelete = () => {},
  aspectWidth,
  aspectHeight,
  isVariant = false,
  imageSizeInBytes,
  disabled,
  isToolTip = false,
  renderToolTip,
  ...props
}) => {
  const inputRef = useRef(null)
  const [isInternalError, setIsInternalError] = useState(false)

  const isError = !value?.length && (isInternalError || error)

  const checkSize = ({ size }) => {
    if (!imageSizeInBytes || size < imageSizeInBytes) {
      return true
    }
    setIsInternalError(`Image Size Exceeded ${bytesToSize(imageSizeInBytes)}`)
  }

  const checkAspectRatio = (file) => {
    let img = new Image()
    const promise = new Promise((resolve, reject) => {
      img.onload = () => {
        const width = img.naturalWidth
        const height = img.naturalHeight
        const ratio = width / height
        if (removeDecimalUpto1(ratio) !== removeDecimalUpto1(aspectWidth / aspectHeight)) {
          setIsInternalError(`Please Upload Image with ${aspectWidth}:${aspectHeight} aspect ratio`)
          return resolve(false)
        }
        resolve(true)
      }
      img.onerror = reject
    })
    img.src = window.URL.createObjectURL(file)

    return promise
  }

  const checkFileType = (file) => {
    const types = ["image/png", "image/jpeg", "image/jpg"]
    if (types.includes(file.type)) {
      return true
    }
    setIsInternalError(`Unsupported File Type`)
    return false
  }

  const checkFileName = (file) => {
    if (file?.name?.length <= 200) {
      return true
    }
    setIsInternalError(`File name exceeded 200 characters`)
    return false
  }

  const onImageUploadClick = async (e) => {
    setIsInternalError(false)
    const { files } = e.target
    if (files?.[0] && checkFileType(files[0]) && checkFileName(files[0]) && checkSize(files[0])) {
      if (!aspectWidth || !aspectHeight) {
        return onChange(files[0])
      }
      const isAspectRatioCorrect = await checkAspectRatio(files[0])
      if (isAspectRatioCorrect) onChange(files[0])
    }
  }

  const viewImage = () => {
    const url = value?.url || URL.createObjectURL(value)
    const newWindow = window.open(url, "_blank", "noopener,noreferrer")
    if (newWindow) newWindow.opener = null
  }

  const showImages = () => {
    if (!Array.isArray(value)) return null

    return value.map((imgFile, index) => {
      let image = {}
      if (imgFile?.url) {
        image.name = decodeURIComponent(imgFile.url)?.split("/")?.pop()
        image.url = imgFile.url
      } else if (imgFile?.name) {
        image.name = imgFile.name
        image.size = imgFile.size
        image.url = URL.createObjectURL(imgFile)
      }

      return (
        <Box key={index} className={styles.imageContainer}>
          <img
            src={image.url ?? TransparentImageIcon}
            alt=""
            className={styles.uploadedImage}
            onClick={() => viewImage(imgFile)}
          />
          <Cancel fontSize="small" onClick={() => onDelete(index)} className={styles.deleteIcon} />
        </Box>
      )
    })
  }

  return (
    <Grid container {...props}>
      {label ? (
        <StyledLabel variant="body1Regular">
          {label}
          {isToolTip && (
            <Tooltip title={renderToolTip}>
              <InfoIcon className="ms-1" fontSize="small"></InfoIcon>
            </Tooltip>
          )}
          {isRequired ? <span>*</span> : null}
        </StyledLabel>
      ) : null}
      <Grid item xs={12}>
        <UploadPreviewFieldContainer className={isError ? "error" : disabled ? "disabled" : ""}>
          <Box className="d-flex w-100 h-100 noImageDataContainer">
            {value?.length > 0 ? (
              <Box className={styles.listImages}>{showImages()}</Box>
            ) : (
              <Box className="placeholder">{placeholder}</Box>
            )}

            {value?.length < 4 && (
              <Box className="d-flex">
                {isVariant ? (
                  <AppButton
                    disabled={disabled}
                    onClick={() => !disabled && inputRef.current?.click()}>
                    Upload
                  </AppButton>
                ) : (
                  <AppButtonGradient
                    variant="filled"
                    disabled={disabled}
                    onClick={() => !disabled && inputRef.current?.click()}>
                    Upload
                  </AppButtonGradient>
                )}
              </Box>
            )}

            <input
              ref={inputRef}
              type="file"
              onChange={onImageUploadClick}
              onClick={(event) => {
                if (value?.length >= 4) {
                  event.preventDefault()
                }
                event.target.value = null
              }}
              style={{ display: "none" }}
              accept="image/png, image/jpeg, image/jpg"
            />
          </Box>
        </UploadPreviewFieldContainer>
      </Grid>
      {isError ? (
        <div style={{ color: "#A9402F", fontSize: "0.75rem", marginTop: 3 }}>
          {isInternalError ? isInternalError : error}
        </div>
      ) : null}
    </Grid>
  )
}

export default ImageUploadPreview
